
import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import React from 'react';
import { planData } from '@/constants/planData';
import { format, addMonths, addDays } from 'date-fns';
import { toast } from 'sonner';
import { asaasService, AsaasCheckoutRequest, AsaasCheckoutResponse } from '@/services/asaasService';
import PaymentLayout from '@/components/payment/PaymentLayout';
import PaymentForm from '@/components/payment/PaymentForm';
import OrderSummary from '@/components/payment/OrderSummary';
import PaymentSuccess from '@/components/payment/PaymentSuccess';



interface CustomerData {
  name: string;
  email: string;
  cpfCnpj: string;
  phone: string;
  postalCode?: string;
  addressNumber?: string;
  addressComplement?: string;
  mobilePhone?: string;
}

interface CheckoutData {
  totalPrice: number;
  plan: string;
  planName: string;
  whatsappAccounts: number;
  extraWhatsappAccounts: number;
  negotiationsIncluded: number;
  extraNegotiationsPacks: number;
  basePrice: number;
  extraWhatsappCost: number;
  extraNegotiationsCost: number;
  customerId: string;
  firebaseCustomerId: string;
  customerData: {
    name: string;
    email: string;
    cpfCnpj: string;
    phone: string;
    postalCode: string;
    address: string;
    addressNumber: string;
    complement: string;
    province: string;
    city: string;
  };
}



export default function Pagamento() {
  const location = useLocation();
  const navigate = useNavigate();
  const checkoutDataFromState = location.state as CheckoutData | undefined;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [checkoutData, setCheckoutData] = useState<CheckoutData | undefined>(checkoutDataFromState);
  const [checkoutResponse, setCheckoutResponse] = useState<AsaasCheckoutResponse | null>(null);

  useEffect(() => {
    // Verificar se temos dados de checkout válidos
    if (!checkoutData) {
      // Tentar recuperar do sessionStorage
      const storedData = sessionStorage.getItem('checkoutData');
      if (storedData) {
        setCheckoutData(JSON.parse(storedData));
      } else {
        toast.error('Erro ao carregar os dados de pagamento. Redirecionando para a tela inicial.');
        setTimeout(() => navigate('/', { replace: true }), 2000);
        return;
      }
    }

    // Verificar se temos o ID do cliente
    if (!checkoutData?.customerId) {
      toast.error('ID do cliente não encontrado. Redirecionando para cadastro.');
      setTimeout(() => navigate('/registro-cliente', { state: { selectedPlan: checkoutData?.plan } }), 2000);
      return;
    }
    
    // Validar se os dados de endereço estão completos
    if (!validateCustomerAddressData(checkoutData)) {
      toast.error('Dados de endereço incompletos. Redirecionando para atualização de cadastro.');
      setTimeout(() => navigate('/registro-cliente', { 
        state: { 
          selectedPlan: checkoutData?.plan,
          customerId: checkoutData?.customerId,
          isUpdate: true
        } 
      }), 2000);
      return;
    }

    // Iniciar o checkout do Asaas
    initAsaasCheckout();
  }, [checkoutData, navigate]);
  
  // Função para validar os dados de endereço do cliente
  const validateCustomerAddressData = (data?: CheckoutData): boolean => {
    if (!data || !data.customerData) return false;
    
    const { address, addressNumber, postalCode, province, city } = data.customerData;
    
    return !!address && !!addressNumber && !!postalCode && !!province && !!city;
  };

  const initAsaasCheckout = async () => {
    if (!checkoutData) return;

    try {
      setIsLoading(true);
      setError(null);

      // Calcular a data de vencimento (hoje)
      const nextDueDate = format(new Date(), 'yyyy-MM-dd');
      
      // Preparar os dados para o checkout do Asaas
      // Seguindo exatamente a documentação: https://docs.asaas.com/reference/criar-novo-checkout
      const asaasCheckoutData: AsaasCheckoutRequest = {
        billingTypes: ['PIX'], // Tipos de pagamento aceitos
        chargeTypes: ['DETACHED'], // Para assinaturas
        minutesToExpire: 15, // 15 minutos para expirar
        callback: {
          // Use the current domain for callbacks
          successUrl: `${window.location.origin}/checkout-success`,
          cancelUrl: `${window.location.origin}/checkout-cancel`,
          expiredUrl: `${window.location.origin}/checkout-expired`,
          autoRedirect: true
        },
        items: [
          {
            description: `Assinatura DGenny - Plano ${checkoutData.planName}`,
            name: `Plano ${checkoutData.planName}`,
            quantity: 1,
            value: checkoutData.totalPrice
          }
        ],
        // Usar apenas o ID do cliente previamente cadastrado no Asaas
        customer: checkoutData.customerId,
        // Referência externa para rastreamento
        externalReference: `dgenny-${new Date().getTime()}`,
        dueDateLimitDays: 1,
        //subscription: {
        //  cycle: 'MONTHLY',
        //  nextDueDate: nextDueDate
        //}
      };

      // Criar o checkout no Asaas
      const response = await asaasService.createCheckout(asaasCheckoutData);
      console.log('Checkout criado com sucesso:', response);
      
      // Garantir que temos uma URL válida antes de prosseguir
      if (!response.checkoutUrl) {
        throw new Error('URL de checkout não recebida do Asaas');
      }
      
      setCheckoutResponse(response);
      setCheckoutUrl(response.checkoutUrl);
      setIsLoading(false);
      
      // Abrir diretamente em nova janela, já que o iframe não é permitido pelo Asaas
      setTimeout(() => {
        openCheckoutInNewWindow();
        console.log('Checkout aberto em nova janela:', response.checkoutUrl);
      }, 500);
    } catch (error) {
      console.error('Erro ao criar checkout:', error);
      setIsLoading(false);
      setError('Erro ao iniciar o checkout. Por favor, tente novamente.');
      toast.error('Erro ao iniciar o checkout. Por favor, tente novamente.');
    }
  };

  const openCheckoutInNewWindow = () => {
    if (checkoutUrl) {
      // Abrir o checkout em uma nova janela/aba
      const checkoutWindow = window.open(checkoutUrl, '_blank');
      
      if (!checkoutWindow) {
        toast.error('O navegador bloqueou a abertura da janela. Por favor, permita popups para este site.');
      } else {
        // Armazenar URL do checkout na sessionStorage para recuperação posterior se necessário
        sessionStorage.setItem('lastCheckoutUrl', checkoutUrl);
        toast.success('Redirecionando para o ambiente de pagamento seguro...');
      }
    } else {
      toast.error('URL de checkout não disponível. Tente novamente.');
    }
  };

  // Lidar com mensagens do iframe (para eventos de sucesso/cancelamento)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verificar a origem da mensagem (deve ser do Asaas)
      if (event.origin.includes('asaas.com')) {
        const { type, data } = event.data;
        
        if (type === 'checkout_success') {
          toast.success('Pagamento processado com sucesso!');
          navigate('/checkout-success', { state: { checkoutData, responseData: data } });
        } else if (type === 'checkout_cancel') {
          toast.info('Checkout cancelado pelo usuário');
          navigate('/checkout-cancel');
        } else if (type === 'checkout_expired') {
          toast.error('O checkout expirou. Por favor, tente novamente.');
          navigate('/checkout-expired');
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [checkoutData, navigate]);

  const handleBack = () => {
    navigate(-1);
  };


  return (
    <>
      <PaymentLayout onBack={handleBack} isProcessing={isLoading}>
        <div className="max-w-4xl mx-auto">
          <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            <h2 className="text-2xl font-bold text-brand-azul mb-4">Finalizar Pagamento</h2>
            
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {error}
                <button 
                  className="bg-brand-roxo text-white px-4 py-2 rounded mt-2 block"
                  onClick={initAsaasCheckout}
                >
                  Tentar novamente
                </button>
              </div>
            )}
            
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-azul mb-4"></div>
                <p className="text-brand-azul/70">Preparando ambiente de pagamento seguro...</p>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-brand-azul/70 mb-6">
                  Clique no botão abaixo para abrir o checkout seguro do Asaas e finalizar seu pagamento.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button 
                    onClick={openCheckoutInNewWindow}
                    className="bg-brand-roxo hover:bg-brand-roxo/90 text-white py-3 px-8 rounded-lg font-medium text-lg"
                    disabled={!checkoutUrl}
                  >
                    Prosseguir para Pagamento
                  </button>
                  
                  {checkoutUrl && (
                    <button 
                      onClick={() => {
                        navigator.clipboard.writeText(checkoutUrl);
                        toast.success('Link copiado para a área de transferência!');
                      }}
                      className="bg-white border-2 border-brand-roxo hover:bg-muted/50 text-brand-azul py-3 px-8 rounded-lg font-medium text-lg"
                    >
                      Copiar Link
                    </button>
                  )}
                </div>
                
                <p className="text-sm text-brand-azul/60 mt-4">
                  Seus dados de pagamento serão processados de forma segura pelo Asaas.
                  <br />
                  Não armazenamos seus dados de cartão de crédito.
                </p>
              </div>
            )}
          </div>
          
          {/* Order Summary */}
          <div>
            <OrderSummary checkoutData={checkoutData} />
          </div>
        </div>
      </PaymentLayout>
    </>
  );
}
